// src/components/TickerPrice.tsx
import React from "react";

interface TickerPriceProps {
  currentPrice: number | null;
  previousPrice: number | null;
  preMarketPrice?: number | null;
  preMarketChange?: number | null;
  postMarketPrice?: number | null;
  postMarketChange?: number | null;
  isPrePostMarket?: boolean;
  className?: string;
}

const TickerPrice: React.FC<TickerPriceProps> = ({ 
  currentPrice, 
  previousPrice,
  preMarketPrice,
  preMarketChange,
  postMarketPrice,
  postMarketChange,
  isPrePostMarket,
  className = "" 
}) => {
  if (currentPrice === null || currentPrice === undefined) {
    return null;
  }

  // Determine color based on price direction
  // Use a small threshold to avoid showing color changes for tiny fluctuations
  const PRICE_CHANGE_THRESHOLD = 0.001; // 0.1 cent threshold (very small to catch real changes)
  let priceColor = "#9ca3af"; // Default gray (muted) - shows when no comparison available or no change
  
  // Only show color if we have a valid previous price to compare against
  if (previousPrice !== null && previousPrice !== undefined && previousPrice > 0 && currentPrice > 0) {
    const priceDiff = currentPrice - previousPrice;
    const percentChange = Math.abs(priceDiff / previousPrice);
    
    // Show color if there's a meaningful change (either absolute > threshold or percent > 0.01%)
    // priceDiff = currentPrice - previousPrice: positive means price went UP (current > previous)
    if (Math.abs(priceDiff) >= PRICE_CHANGE_THRESHOLD || percentChange > 0.0001) {
      if (priceDiff > 0) {
        priceColor = "#22c55e"; // Green for up
      } else if (priceDiff < 0) {
        priceColor = "#ef4444"; // Red for down
      }
      // If priceDiff === 0, keep gray (no change)
    }
  }

  // Format price to 2 decimal places
  const formattedPrice = currentPrice.toFixed(2);

  // Determine pre/post-market price color
  let preMarketColor = "#9ca3af"; // Default gray
  let postMarketColor = "#9ca3af"; // Default gray
  if (preMarketChange !== null && preMarketChange !== undefined) {
    if (preMarketChange > 0) {
      preMarketColor = "#22c55e"; // Green for up
    } else if (preMarketChange < 0) {
      preMarketColor = "#ef4444"; // Red for down
    }
  }
  if (postMarketChange !== null && postMarketChange !== undefined) {
    if (postMarketChange > 0) {
      postMarketColor = "#22c55e"; // Green for up
    } else if (postMarketChange < 0) {
      postMarketColor = "#ef4444"; // Red for down
    }
  }

  return (
    <span className={`ticker-price ${className}`}>
      <span
        style={{
          fontSize: "0.75em",
          color: priceColor,
          fontWeight: 500,
          marginLeft: "8px",
          display: "inline-block",
          verticalAlign: "baseline",
        }}
      >
        ${formattedPrice}
      </span>
      {(() => {
        // Check current market status to determine if we should show PM/AH price
        const now = new Date();
        const etTime = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
        const dayOfWeek = etTime.getDay(); // 0 = Sunday, 6 = Saturday
        const hours = etTime.getHours();
        const minutes = etTime.getMinutes();
        const timeInMinutes = hours * 60 + minutes;
        
        // Determine current market status
        let isNormalMarketHours = false;
        if (dayOfWeek !== 0 && dayOfWeek !== 6) { // Not weekend
          // Normal market hours: 9:30 AM - 4:00 PM ET (570-960 minutes)
          isNormalMarketHours = timeInMinutes >= 570 && timeInMinutes < 960;
        }
        
        // Determine if we're in post-market hours (4:00 PM - 8:00 PM ET)
        const isPostMarketHours = (dayOfWeek !== 0 && dayOfWeek !== 6) && (timeInMinutes >= 960 && timeInMinutes < 1200);
        
        // Show post-market (AH) price if available and we're in post-market hours
        if (!isNormalMarketHours && isPrePostMarket && postMarketPrice !== null && postMarketPrice !== undefined && isPostMarketHours) {
          return (
            <span
              style={{
                fontSize: "0.65em",
                color: postMarketColor,
                fontWeight: 400,
                marginLeft: "6px",
                display: "inline-block",
                verticalAlign: "baseline",
                opacity: 0.8,
              }}
              title={`After-hours: $${postMarketPrice.toFixed(2)} (${postMarketChange !== null && postMarketChange !== undefined ? (postMarketChange >= 0 ? '+' : '') + postMarketChange.toFixed(2) : 'N/A'})`}
            >
              AH: ${postMarketPrice.toFixed(2)}
            </span>
          );
        }
        
        // Show pre-market (PM) price if available and we're NOT in normal market hours
        if (!isNormalMarketHours && isPrePostMarket && preMarketPrice !== null && preMarketPrice !== undefined) {
          return (
            <span
              style={{
                fontSize: "0.65em",
                color: preMarketColor,
                fontWeight: 400,
                marginLeft: "6px",
                display: "inline-block",
                verticalAlign: "baseline",
                opacity: 0.8,
              }}
              title={`Pre-market: $${preMarketPrice.toFixed(2)} (${preMarketChange !== null && preMarketChange !== undefined ? (preMarketChange >= 0 ? '+' : '') + preMarketChange.toFixed(2) : 'N/A'})`}
            >
              PM: ${preMarketPrice.toFixed(2)}
            </span>
          );
        }
        
        return null;
      })()}
    </span>
  );
};

export default TickerPrice;

